import React, { useMemo } from 'react';
import { Check, X, Shield, ShieldAlert, ShieldCheck, ShieldOff } from 'lucide-react';

const PasswordStrengthMeter = ({ password }) => {
  const requirements = useMemo(() => [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Uppercase letter (A-Z)', met: /[A-Z]/.test(password) },
    { label: 'Lowercase letter (a-z)', met: /[a-z]/.test(password) },
    { label: 'Number (0-9)', met: /[0-9]/.test(password) },
    { label: 'Special character (!@#$...)', met: /[^A-Za-z0-9]/.test(password) },
  ], [password]);

  const metCount = requirements.filter(r => r.met).length;
  const allMet = metCount === requirements.length;

  const strength = useMemo(() => {
    if (password.length === 0) return { label: '', color: 'bg-gray-200', textColor: 'text-gray-400', width: '0%', icon: null };
    if (metCount <= 1) return { label: 'Very Weak', color: 'bg-red-500', textColor: 'text-red-500', width: '20%', icon: ShieldOff };
    if (metCount === 2) return { label: 'Weak', color: 'bg-orange-500', textColor: 'text-orange-500', width: '40%', icon: ShieldAlert };
    if (metCount === 3) return { label: 'Fair', color: 'bg-yellow-500', textColor: 'text-yellow-500', width: '60%', icon: ShieldAlert };
    if (metCount === 4) return { label: 'Good', color: 'bg-blue-500', textColor: 'text-blue-500', width: '80%', icon: Shield };
    return { label: 'Strong', color: 'bg-green-500', textColor: 'text-green-500', width: '100%', icon: ShieldCheck };
  }, [password, metCount]);

  if (password.length === 0) return null;

  const StrengthIcon = strength.icon;

  return (
    <div className="mt-3 space-y-3">
      {/* Strength bar */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center space-x-1.5">
            {StrengthIcon && <StrengthIcon className={`w-4 h-4 ${strength.textColor}`} />}
            <span className={`text-xs font-semibold ${strength.textColor}`}>{strength.label}</span>
          </div>
          <span className="text-xs text-gray-400">{metCount}/{requirements.length}</span>
        </div>
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${strength.color} rounded-full transition-all duration-500 ease-out`}
            style={{ width: strength.width }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="grid grid-cols-1 gap-1">
        {requirements.map((req, index) => (
          <div
            key={index}
            className={`flex items-center space-x-2 text-xs transition-colors duration-200 ${
              req.met ? 'text-green-600' : 'text-gray-400'
            }`}
          >
            {req.met ? (
              <Check className="w-3.5 h-3.5 flex-shrink-0" />
            ) : (
              <X className="w-3.5 h-3.5 flex-shrink-0" />
            )}
            <span className={req.met ? 'font-medium' : ''}>{req.label}</span>
          </div>
        ))}
      </div>

      {allMet && (
        <div className="flex items-center space-x-1.5 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span className="font-medium">Password meets all requirements</span>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
